import { useState } from 'react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { List, Check, Star } from 'lucide-react';
import type { MaterialPriceListEntry } from '@/features/materials/types/material.types';
import { cn } from '@/lib/utils/cn';
import { formatNumber } from '@/lib/utils/format';

interface PriceListPickerProps {
  entries: MaterialPriceListEntry[];
  currentPrice: number | undefined;
  onSelectPrice: (price: number, listName: string) => void;
  currencySymbol?: string;
}

export function PriceListPicker({
  entries,
  currentPrice,
  onSelectPrice,
  currencySymbol = '$',
}: PriceListPickerProps) {
  const [open, setOpen] = useState(false);

  if (!entries || entries.length === 0) return null;

  const sorted = [...entries].sort((a, b) => {
    if (a.priceList.isDefault && !b.priceList.isDefault) return -1;
    if (!a.priceList.isDefault && b.priceList.isDefault) return 1;
    return a.priceList.name.localeCompare(b.priceList.name);
  });

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-7 w-7 shrink-0"
          title="Ver precios en listas de precios"
        >
          <List className="h-3.5 w-3.5 text-info" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[280px] p-0" align="end" side="bottom">
        <div className="px-3 py-2 border-b">
          <p className="text-xs font-semibold">Listas de Precios</p>
          <p className="text-[10px] text-muted-foreground">
            Seleccione un precio para aplicarlo al campo de costo
          </p>
        </div>
        <div className="max-h-[200px] overflow-y-auto p-1">
          {sorted.map((entry) => {
            const isActive = currentPrice != null && Math.abs(currentPrice - entry.price) < 0.001;
            return (
              <button
                key={entry.id}
                type="button"
                className={cn(
                  'relative flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-xs outline-none hover:bg-accent hover:text-accent-foreground cursor-pointer',
                  isActive && 'bg-accent'
                )}
                onClick={() => {
                  onSelectPrice(entry.price, entry.priceList.name);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn('h-3 w-3 shrink-0', isActive ? 'opacity-100 text-ok' : 'opacity-0')}
                />
                <div className="flex-1 min-w-0 flex items-center gap-1.5">
                  <span className="truncate">{entry.priceList.name}</span>
                  {entry.priceList.isDefault && (
                    <Star className="h-3 w-3 text-alerta fill-alerta shrink-0" />
                  )}
                </div>
                <span className="font-mono font-semibold text-ok tabular-nums shrink-0">
                  {currencySymbol} {formatNumber(entry.price)}
                </span>
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
